import React from "react";
import { ErrorMessage, Field, getIn, useFormikContext } from "formik";

import { Card } from "../../../Card";
import { UserTextInputFields } from "../../../Modals/UserTextInputPluginForm";
import { Input } from "../../../../sudokit/components/Input";
import { getLiquidReferencedVariables } from "../../../../../../src/shared/schemas/PluginSchema";
import {
  DOCUMENT_VARIABLE_SETTINGS,
  EDITABLE_VARIABLE_ORDER,
  getVariableIconPath,
  normalizeLiquidVariableName,
} from "../inputSettings";
import { Container } from "./styles";
import { variableLabel } from "../utils";

const preventInvalidNumberKey = (e) => {
  if (["e", "E", "-", "+", "."].includes(e.key)) {
    e.preventDefault();
  }
};

const parseImmediateWordCountValue = (value) => {
  if (value === "") return "";
  const parsed = parseInt(value, 10);
  if (Number.isNaN(parsed)) return "";
  return Math.max(0, parsed);
};

const isWordCountValue = (value) => value !== "" && value !== null && value !== undefined && !Number.isNaN(Number(value));

const toDomId = (value) => String(value).replace(/[^a-zA-Z0-9_-]/g, "-");

const getReferencedEditableVariables = (blocks) => {
  const usedVariables = new Set();

  blocks
    .filter((block) => block.blockType === "inference")
    .forEach((block) => {
      const messages = block.options?.prompt?.messages || [];
      messages.forEach((message) => {
        if (!message?.template) return;
        getLiquidReferencedVariables(message.template).forEach((variable) => {
          const normalized = normalizeLiquidVariableName(variable);
          if (EDITABLE_VARIABLE_ORDER.includes(normalized)) {
            usedVariables.add(normalized);
          }
        });
      });
    });

  return usedVariables;
};

const WordCountField = ({ fieldPath, minWordsPath, maxWordsPath, wordCountType, label, helper, placeholder, ariaLabel }) => {
  const inputId = `${toDomId(fieldPath)}-input`;
  const labelId = `${toDomId(fieldPath)}-label`;
  const helperId = `${toDomId(fieldPath)}-helper`;

  const updateWordCount = (form, nextValue) => {
    if (wordCountType === "minWords") {
      form.setFieldValue(fieldPath, nextValue);

      const currentMaxWords = getIn(form.values, maxWordsPath);
      if (isWordCountValue(nextValue) && isWordCountValue(currentMaxWords) && nextValue > Number(currentMaxWords)) {
        form.setFieldValue(maxWordsPath, nextValue);
      }
      return;
    }

    const currentMinWords = getIn(form.values, minWordsPath);
    const constrainedMaxWords =
      isWordCountValue(nextValue) && isWordCountValue(currentMinWords)
        ? Math.max(nextValue, Number(currentMinWords))
        : nextValue;

    form.setFieldValue(fieldPath, constrainedMaxWords);
  };

  return (
    <div className="inputSettingField">
      <label htmlFor={inputId}>
        <h3 id={labelId}>{label}</h3>
        <p id={helperId}>{helper}</p>
      <Field name={fieldPath}>
        {({ field, form, meta }) => (
          <Input
            id={inputId}
            {...field}
            type="number"
            mode="default"
            min={0}
            step="100"
            error={meta.error && meta.touched}
            aria-describedby={helperId}
            aria-invalid={Boolean(meta.error && meta.touched)}
            onChange={(e) => updateWordCount(form, parseImmediateWordCountValue(e.target.value))}
            onWheel={(e) => e.currentTarget.blur()}
            onKeyDown={preventInvalidNumberKey}
            placeholder={`${placeholder}`}
            aria-label={ariaLabel}
          />
        )}
      </Field>
      <ErrorMessage
        name={fieldPath}
        component="p"
        className="error"
      />
      </label>
    </div>
  );
};

const UserTextInstructionsField = ({ fieldPath, pluginName }) => {
  const inputId = `${toDomId(fieldPath)}-input`;
  const labelId = `${toDomId(fieldPath)}-label`;
  const helperId = `${toDomId(fieldPath)}-helper`;

  return (
    <div className="userTextCustomize">
      <Field name={fieldPath}>
        {({ field, meta }) => {
          const previewInstructions = String(field.value || "").trim() || "Give instructions";
          return (
            <>
              <label htmlFor={inputId}>
                <h3 id={labelId}>Customize your Pop-up</h3>
                <p id={helperId}>{DOCUMENT_VARIABLE_SETTINGS.user_text_input.instructionsHelper}</p>
              <Input
                id={inputId}
                {...field}
                mode="default"
                error={meta.error && meta.touched}
                placeholder={DOCUMENT_VARIABLE_SETTINGS.user_text_input.instructionsPlaceholder}
                className="userTextInstructionInput"
                aria-label="User instructions prompt text"
                aria-describedby={helperId}
                aria-invalid={Boolean(meta.error && meta.touched)}
              />
              <ErrorMessage
                name={fieldPath}
                component="p"
                className="error"
              />
              </label>
            <div className="userTextPreview">
              <div>
                <h3>Preview</h3>
                <p>Shows how the instructions popup will look when someone runs the plugin.</p>
              </div>
              <Card>
                <UserTextInputFields
                  name={pluginName?.trim() || "Your Plugin"}
                  instructions={previewInstructions}
                  userTextInput="George|"
                  disabled
                />
              </Card>
            </div>
            </>
          );
        }}
      </Field>
    </div>
  );
};

const VariableChicletHeading = ({ variableName }) => (
  <h3 className="inputSettingVariableHeading">
    <span
      className="inputSettingVariableChiclet"
      data-variable-id={variableName}
    >
      <span
        aria-hidden="true"
        className="inputSettingVariableChicletIcon"
        style={{ "--input-setting-variable-icon": `url("/assets/${getVariableIconPath(variableName)}")` }}
      />
      <span className="inputSettingVariableChicletLabel">
        {variableLabel(variableName)}
      </span>
    </span>
  </h3>
);

const InputSettings = () => {
  const { values } = useFormikContext();
  const blocks = values.blocks || [];
  const inputDocumentBlockIndex = blocks.findIndex((block) => block.blockType === "input_document");
  const inputDocumentBlock = blocks[inputDocumentBlockIndex];
  const referencedEditableVariables = getReferencedEditableVariables(blocks);
  const outputBySource = new Map(
    (inputDocumentBlock?.options?.outputs || []).map((output, outputIndex) => [
      output.source,
      { output, outputIndex },
    ])
  );

  const rows = EDITABLE_VARIABLE_ORDER
    .filter((source) => referencedEditableVariables.has(source))
    .map((source) => outputBySource.get(source))
    .filter(Boolean);

  if (inputDocumentBlockIndex < 0 || rows.length === 0) {
    return null;
  }

  return (
    <Container>
      <h2 className="inputSettingsHeading">Input Settings</h2>
      {rows.map(({ output, outputIndex }) => {
        const config = DOCUMENT_VARIABLE_SETTINGS[output.source];
        const outputPath = `blocks[${inputDocumentBlockIndex}].options.outputs[${outputIndex}]`;
        const minWordsPath = `${outputPath}.minWords`;
        const maxWordsPath = `${outputPath}.maxWords`;
        const title = config.title;

        return (
          <div
            key={output.source}
            className="inputSettingRow"
          >
            <div className="inputSettingHeader">
              <VariableChicletHeading variableName={output.source} />
              <p>{config.description}</p>
            </div>
            {output.source === "user_text_input" && (
              <UserTextInstructionsField
                fieldPath={`${outputPath}.instructionsText`}
                pluginName={values.name}
              />
            )}
            <div className="inputSettingFields">
              <WordCountField
                fieldPath={minWordsPath}
                minWordsPath={minWordsPath}
                maxWordsPath={maxWordsPath}
                wordCountType="minWords"
                label="Minimum Words"
                helper={config.minWordsHelper}
                placeholder={config.minWords}
                ariaLabel={`Minimum words for ${title}`}
              />
              <WordCountField
                fieldPath={maxWordsPath}
                minWordsPath={minWordsPath}
                maxWordsPath={maxWordsPath}
                wordCountType="maxWords"
                label="Maximum Words"
                helper={config.maxWordsHelper}
                placeholder={config.maxWords}
                ariaLabel={`Maximum words for ${title}`}
              />
            </div>
          </div>
        );
      })}
    </Container>
  );
};

export default InputSettings;
